import { BestTimeToPostChartView } from './BestTimeToPostChart.view';
import React from 'react';
import {
  GetBestTimePostStatsQueryHookResult,
  PostEngagementByDayOfWeek,
  PostEngagementByHourOfDay,
  StartOfWeekFormatSetting,
  TimeFormatSetting
} from '../../../../../models/graphql';
import { useSettings } from '../../../../../hooks/useSettings';

type keysOfDayWeek = Exclude<keyof PostEngagementByDayOfWeek, '__typename'>;
type keysOfHours = Exclude<keyof PostEngagementByHourOfDay, '__typename'>;
const createChartByWeekObj = (
  byWeekDay: PostEngagementByDayOfWeek | undefined,
  startOfWeek: StartOfWeekFormatSetting
) => {
  if (!byWeekDay) {
    return null;
  }

  const weeksDay = Object.keys(byWeekDay) as Array<keysOfDayWeek>;
  weeksDay.shift(); // removes typename prop
  if (startOfWeek === StartOfWeekFormatSetting.SUNDAY) {
    const sunday = weeksDay.pop();
    sunday && weeksDay.unshift(sunday);
  }

  return weeksDay
    .map(weekDay => ({
      weekDay: weekDay.charAt(0).toUpperCase() + weekDay.slice(1),
      engagement: byWeekDay[weekDay]
    }))
    .map(data => ({ weekDay: data.weekDay.substring(0, 3), engagement: data.engagement }));
};

const createCharByHoursObj = (byHours: PostEngagementByHourOfDay | undefined) => {
  if (!byHours) {
    return null;
  }
  const hoursKeys = Object.keys(byHours) as Array<keysOfHours>;
  hoursKeys.shift(); // removes typename prop

  return hoursKeys.map(hour => ({
    hour: hour.split('hour')[1],
    engagement: byHours[hour]
  }));
};
type BestObj = { value: number; bestDay?: string; hourKey: string };
export type BestTimesType = {
  bestTime: BestObj | null;
  secondBestTime: BestObj | null;
  thirdBestTime: BestObj | null;
  bestDay: string;
};

export type ChartDataByHours = { hour: string; engagement: number | undefined | null };
export type ChartDataByWeek = { weekDay: string; engagement: number | undefined | null };

const getBestTimeInfo = (
  chartDataByWeek: ChartDataByWeek[] | null,
  chartDataByHours: ChartDataByHours[] | null,
  hourFormat: TimeFormatSetting
): BestTimesType => {
  if (!chartDataByWeek || !chartDataByHours) {
    return { bestTime: null, secondBestTime: null, thirdBestTime: null, bestDay: '' };
  }
  const { bestDay } = chartDataByWeek.reduce(
    (acc, chartPoint) => {
      const value = chartPoint.engagement ?? 0;
      const weekDayKey = chartPoint.weekDay;

      if (value > acc.value) {
        acc.bestDay = weekDayKey;
        acc.value = value;
      }
      return acc;
    },
    { bestDay: '', value: -Infinity }
  );

  const { bestTime, secondBestTime, thirdBestTime } = chartDataByHours.reduce(
    (acc, chartPoint) => {
      const value = chartPoint.engagement ?? 0;
      const is12hFormat = hourFormat === TimeFormatSetting.HOUR_12;
      const hourKey = is12hFormat ? formatTickTo12h(chartPoint.hour) : formatTickTo24h(chartPoint.hour);

      if (value > acc.bestTime.value) {
        acc.thirdBestTime = acc.secondBestTime;
        acc.secondBestTime = acc.bestTime;
        acc.bestTime = { hourKey, value };
      } else if (value > acc.secondBestTime.value) {
        acc.thirdBestTime = acc.secondBestTime;
        acc.secondBestTime = { hourKey, value };
      } else if (value > acc.thirdBestTime.value) {
        acc.thirdBestTime = { hourKey, value };
      }

      return acc;
    },
    {
      bestTime: { hourKey: '', value: -Infinity },
      secondBestTime: { hourKey: '', value: -Infinity },
      thirdBestTime: { hourKey: '', value: -Infinity }
    }
  );

  return { bestTime, secondBestTime, thirdBestTime, bestDay };
};

export const formatTickTo12h = (hour: string) => {
  const hourInt = parseInt(hour, 10);
  return hourInt >= 12 ? `${hourInt === 12 ? 12 : hourInt % 12} PM` : `${hourInt || 12} AM`;
};
export const formatTickTo24h = (hour: string) => {
  const hourInt = parseInt(hour, 10);
  return hourInt <= 9 ? `0${hourInt}:00` : `${hourInt}:00`;
};

const isChartEmpty = (chartByHours: ChartDataByHours[] | null, chartByDays: ChartDataByWeek[] | null) => {
  const isChartHoursEmpty = chartByHours?.every(data => data.engagement === null) ?? true;
  const isChartDaysEmpty = chartByDays?.every(data => data.engagement === null) ?? true;
  return isChartHoursEmpty && isChartDaysEmpty;
};
export const BestTimeToPostChartController = ({
  data,
  loading
}: {
  data: GetBestTimePostStatsQueryHookResult['data'];
  loading: boolean;
}) => {
  const resultData = data?.analyticsBestTimeToPostStats;
  const { selectedOrgSettings } = useSettings();
  const byWeekDay = resultData?.postEngagementByDayOfWeek;

  const chartByHours = createCharByHoursObj(resultData?.postEngagementByHourOfDay);
  const startOfWeek = selectedOrgSettings?.startOfWeekFormat ?? StartOfWeekFormatSetting.SUNDAY;
  const chartByWeekday = createChartByWeekObj(byWeekDay, startOfWeek);
  const hourFormat = selectedOrgSettings?.timeFormat ?? TimeFormatSetting.HOUR_24;
  const bestTimeObj = getBestTimeInfo(chartByWeekday, chartByHours, hourFormat);
  const bestDayFormattedString =
    byWeekDay && bestTimeObj && Object.keys(byWeekDay).find(day => day.includes(bestTimeObj.bestDay.toLowerCase()));

  return (
    <BestTimeToPostChartView
      loading={loading}
      bestTimeObj={bestTimeObj}
      chartByHours={chartByHours}
      chartByWeekday={chartByWeekday}
      hourFormat={hourFormat}
      bestDayFormattedString={bestDayFormattedString}
      isEmpty={isChartEmpty(chartByHours, chartByWeekday)}></BestTimeToPostChartView>
  );
};
